import React, { useState } from 'react';
import { Check, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { pricingPlans } from '../../data/mock';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Pricing = ({ onDemoClick }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Profesyonel, 1 = Kurumsal
  const { ref: sectionRef, isVisible } = useScrollAnimation();

  const PricingCard = ({ plan, className = '' }) => (
    <div
      className={`relative rounded-2xl p-6 md:p-8 transition-all duration-500 hover-lift ${
        plan.popular
          ? 'bg-gradient-to-br from-[#C9A97B]/20 via-[#1F1914] to-[#1A1512] border-2 border-[#C9A97B]/50'
          : 'bg-gradient-to-br from-[#1F1914] to-[#1A1512] border border-[#C9A97B]/10'
      } ${className}`}
    >
      {/* Popular Badge - Desktop Only */}
      {plan.popular && (
        <div className="hidden md:block absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#C9A97B] text-[#1A1512] text-sm font-semibold">
            <Star className="w-4 h-4 fill-current" />
            En Popüler
          </div>
        </div>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6 md:mb-8">
        <h3 className="text-xl md:text-2xl font-display font-bold text-[#F5F0E8] mb-1">
          {plan.name}
        </h3>
        <p className="text-[#7A7067] text-sm">{plan.subtitle}</p>
      </div>

      {/* Price */}
      <div className="text-center mb-6 md:mb-8">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-xl md:text-2xl text-[#C9A97B]">{plan.currency}</span>
          <span className="text-4xl md:text-5xl font-display font-bold text-gold-gradient">
            {plan.price}
          </span>
          <span className="text-[#7A7067]">{plan.period}</span>
        </div>
      </div>

      {/* Features */}
      <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-[#C9A97B]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-[#C9A97B]" />
            </div>
            <span className="text-[#BFB5A5] text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <Button
        onClick={onDemoClick}
        className={`w-full py-3 md:py-4 rounded-xl font-semibold transition-all ${
          plan.popular
            ? 'btn-gold'
            : 'bg-[#251E18] text-[#F5F0E8] hover:bg-[#2A231D] border border-[#C9A97B]/20'
        }`}
      >
        Demo Talep Et
      </Button>
    </div>
  );

  return (
    <section id="pricing" ref={sectionRef} className="py-16 md:py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0B09] via-[#1A1512] to-[#0D0B09]" />
      
      {/* Decorative */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#C9A97B]/3 rounded-full blur-[200px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-10 md:mb-16 ${isVisible ? 'animate-fadeInDown' : 'opacity-0'}`}>
          <span className={`inline-block px-4 py-1.5 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-sm font-medium mb-4 ${isVisible ? 'animate-bounceIn' : 'opacity-0'}`} style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
            Fiyatlandırma
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#F5F0E8] mb-4">
            Kuyumcu İşletmenize <span className="text-gold-gradient">Uygun Planı</span> Seçin
          </h2>
          <p className="text-[#BFB5A5] text-base md:text-lg max-w-2xl mx-auto">
            Tüm planlarda canlı borsa entegrasyonu, sınırsız kullanıcı ve ücretsiz kurulum dahildir.
          </p>
        </div>

        {/* Mobile Toggle Switch */}
        <div className="md:hidden mb-8">
          <div className="relative bg-[#1A1512] p-1.5 rounded-2xl border border-[#C9A97B]/20 max-w-sm mx-auto">
            {/* Sliding Background */}
            <div 
              className="absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-gradient-to-r from-[#C9A97B] to-[#D4BC94] rounded-xl transition-all duration-500 ease-out shadow-lg"
              style={{ 
                left: activeTab === 0 ? '6px' : 'calc(50% + 0px)',
              }}
            />
            
            {/* Toggle Buttons */}
            <div className="relative flex">
              <button
                onClick={() => setActiveTab(0)}
                className={`flex-1 py-3.5 px-4 rounded-xl text-sm font-semibold transition-all duration-500 relative z-10 ${
                  activeTab === 0 
                    ? 'text-[#1A1512]' 
                    : 'text-[#7A7067] hover:text-[#BFB5A5]'
                }`}
              >
                Profesyonel
              </button>
              <button
                onClick={() => setActiveTab(1)}
                className={`flex-1 py-3.5 px-4 rounded-xl text-sm font-semibold transition-all duration-500 relative z-10 ${
                  activeTab === 1 
                    ? 'text-[#1A1512]' 
                    : 'text-[#7A7067] hover:text-[#BFB5A5]'
                }`}
              >
                Kurumsal
              </button>
            </div>
          </div>

          {/* Active Plan Badge - Mobile */}
          <div className="flex justify-center mt-4">
            {activeTab === 1 && (
              <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#C9A97B]/20 text-[#C9A97B] text-xs font-medium animate-fade-in-up">
                <Star className="w-3 h-3 fill-current" />
                En Popüler Seçim
              </div>
            )}
          </div>
        </div>

        {/* Mobile Single Card View */}
        <div className="md:hidden">
          <div className="relative overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${activeTab * 100}%)` }}
            >
              {pricingPlans.map((plan) => (
                <div key={plan.id} className="w-full flex-shrink-0 px-1">
                  <PricingCard plan={plan} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop Pricing Cards */}
        <div className="hidden md:grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div 
              key={plan.id}
              className={isVisible ? (index === 0 ? 'animate-slideInLeft' : 'animate-slideInRight') : 'opacity-0'}
              style={{ animationDelay: `${index * 200 + 300}ms`, animationFillMode: 'forwards' }}
            >
              <PricingCard plan={plan} />
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className={`mt-8 md:mt-12 text-center ${isVisible ? 'animate-fadeIn' : 'opacity-0'}`} style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}>
          <p className="text-[#BFB5A5] text-sm mt-2">
            Özel ihtiyaçlarınız için <button onClick={onDemoClick} className="text-[#C9A97B] hover:underline">bizimle iletişime geçin</button>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
